// The Support inbox — SOS alerts and support tickets in one list (0315, BL-16;
// Leon 2026-10-10).
//
// ⚠ DATA WALL. Tickets come from `v_support_thread_staff`, which carries the
// client's FIRST NAME ONLY and no client id — never phone, email or address.
// Nothing here may widen that; a hub coordinator must not be able to learn more
// about a client from this page than the view gives.
//
// Urgency is the SERVER's answer (computed in the view at read time). This file
// only orders and labels it; it never decides whether something is urgent.

import type { SosAlertView } from "@/features/sos/sos.model";

export type SupportCategory = "payment" | "booking" | "account" | "other";
export type SupportStatus = "ai" | "waiting" | "open" | "resolved";

/** One row of `v_support_thread_staff` as this page reads it. */
export interface SupportTicketRow {
  id: string;
  ref: string;
  hub_id: string | null;
  booking_id: string | null;
  status: SupportStatus;
  assigned_staff_id: string | null;
  client_first_name: string | null;
  /** 0320 — "staff" when a therapist or rider opened the thread. */
  author: "client" | "staff";
  staff_id: string | null;
  staff_first_name: string | null;
  staff_role: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  /**
   * Set when the CLIENT closed their own request (0172/0173). `status` is left
   * as the tier it was in, so a closed-by-client ticket still reads "waiting" —
   * this column is the only thing that says it is over.
   */
  withdrawn_at: string | null;
  category: SupportCategory;
  urgent: boolean;
  urgent_reason: string | null;
  urgent_override: boolean | null;
}

export const CATEGORY_LABEL: Record<SupportCategory | "sos", string> = {
  sos: "SOS",
  payment: "Payment",
  booking: "Booking",
  account: "Account",
  other: "Other",
};

const URGENT_REASON_LABEL: Record<string, string> = {
  payment: "Payment",
  booking_in_progress: "Booking in progress",
  starting_within_hour: "Starts within the hour",
  flagged: "Flagged by an admin",
};

export function urgentReasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  return URGENT_REASON_LABEL[reason] ?? "Urgent";
}

/** Over, one way or the other: the team resolved it, or the client closed it. */
export function isClosed(ticket: Pick<SupportTicketRow, "status" | "withdrawn_at">): boolean {
  return ticket.status === "resolved" || ticket.withdrawn_at !== null;
}

/** What to call a ticket's state, with "closed by the client" winning over the stale tier. */
export function ticketStatusLabel(
  ticket: Pick<SupportTicketRow, "status" | "withdrawn_at">,
): string {
  return ticket.withdrawn_at !== null && ticket.status !== "resolved"
    ? "Closed by client"
    : STATUS_LABEL[ticket.status];
}

export const STATUS_LABEL: Record<SupportStatus, string> = {
  ai: "With the assistant",
  waiting: "Waiting for the team",
  open: "Being handled",
  resolved: "Resolved",
};

/** The newest message of a thread — the Messenger-style preview line. */
export interface LastMessage {
  body: string;
  created_at: string;
  sender: "client" | "ai" | "staff";
}

interface InboxBase {
  id: string;
  ref: string;
  category: SupportCategory | "sos";
  urgent: boolean;
  urgentLabel: string | null;
  who: string;
  bookingRefs: string[];
  statusLabel: string;
  /** ISO time of the last thing that happened — what "most recent" sorts on. */
  lastActivity: string;
  /** The newest message, when the thread has one. SOS alerts have none. */
  lastMessage: LastMessage | null;
  /** Whose inbox it belongs to — what the hub filter reads. */
  hubId: string | null;
  /** Who holds it. Null for an unclaimed ticket and for every SOS (it has a handler name only). */
  assignedStaffId: string | null;
}

export type InboxItem =
  | (InboxBase & { kind: "sos"; sos: SosAlertView })
  | (InboxBase & { kind: "ticket"; ticket: SupportTicketRow });

/**
 * What the inbox shows by default (Leon): escalated tickets — the client asked
 * for a person — and anything urgent. "Show all" adds the assistant tier and
 * resolved tickets. SOS alerts are always shown; resolved SOS never reach here.
 */
export function shownByDefault(item: InboxItem): boolean {
  if (item.kind === "sos") return true;
  return item.urgent || item.ticket.status === "waiting" || item.ticket.status === "open";
}

/** A ticket that is over — resolved by the team or closed by the client. SOS alerts never are. */
function isClosedItem(item: InboxItem): boolean {
  return item.kind === "ticket" && isClosed(item.ticket);
}

export type InboxFilter = "open" | "resolved" | "all";

/**
 * Messenger-style filter. `open` is the old default — live SOS, escalated
 * tickets and anything urgent — minus anything resolved; `resolved` is what was
 * closed (and can be reopened); `all` is everything, including tickets still
 * with the assistant. A resolved item never counts as open even if it was urgent.
 */
export function matchesFilter(item: InboxItem, filter: InboxFilter): boolean {
  if (filter === "all") return true;
  const closed = isClosedItem(item);
  return filter === "resolved" ? closed : !closed && shownByDefault(item);
}

// ── helpdesk queues and filters ────────────────────────────────────────────

/**
 * The quick views down the left: what a support desk actually works from.
 *   open        live SOS, escalated and urgent tickets (never a resolved one)
 *   unassigned  open, and nobody has it — the "someone pick this up" queue
 *   mine        open and held by the person looking
 *   urgent      open and urgent
 *   resolved    closed — resolved by the team (can be reopened) or closed by the client (kept for the record)
 *   all         everything, including tickets still with the assistant
 */
export type Queue = "open" | "unassigned" | "mine" | "urgent" | "resolved" | "all";

export const QUEUES: readonly { value: Queue; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "unassigned", label: "Unassigned" },
  { value: "mine", label: "Mine" },
  { value: "urgent", label: "Urgent" },
  { value: "resolved", label: "Closed" },
  { value: "all", label: "All" },
];

export interface InboxCriteria {
  queue: Queue;
  /** "" = any. */
  category: SupportCategory | "sos" | "";
  /** "" = any. A ticket status; SOS alerts have none, so they drop out when this is set. */
  status: SupportStatus | "";
  /** A hub id, "" = any. */
  hub: string;
  /** `recent` is the default; `oldest` surfaces what has waited longest. Urgent stays pinned either way. */
  sort: "recent" | "oldest";
}

export const DEFAULT_CRITERIA: InboxCriteria = {
  queue: "open",
  category: "",
  status: "",
  hub: "",
  sort: "recent",
};

export function inQueue(item: InboxItem, queue: Queue, myId: string | undefined): boolean {
  switch (queue) {
    case "all":
      return true;
    case "resolved":
      return matchesFilter(item, "resolved");
    case "open":
      return matchesFilter(item, "open");
    case "unassigned":
      return (
        matchesFilter(item, "open") &&
        (item.kind === "sos" ? item.sos.stage === "open" : item.assignedStaffId === null)
      );
    case "mine":
      return (
        matchesFilter(item, "open") &&
        item.kind === "ticket" &&
        myId !== undefined &&
        item.assignedStaffId === myId
      );
    case "urgent":
      return matchesFilter(item, "open") && item.urgent;
  }
}

/** Filter, then order. Urgent is pinned to the top in both sorts. */
export function filterInbox(
  items: readonly InboxItem[],
  criteria: InboxCriteria,
  myId: string | undefined,
): InboxItem[] {
  const kept = items.filter(
    (item) =>
      inQueue(item, criteria.queue, myId) &&
      (criteria.category === "" || item.category === criteria.category) &&
      (criteria.status === "" ||
        // A client-closed ticket keeps its old tier in `status`, so it must not
        // match "Waiting" or "Being handled" — it is closed, not waiting.
        (item.kind === "ticket" &&
          item.ticket.status === criteria.status &&
          (item.ticket.withdrawn_at === null || item.ticket.status === "resolved"))) &&
      (criteria.hub === "" || item.hubId === criteria.hub),
  );
  if (criteria.sort === "recent") return kept.sort(compareInbox);
  return kept.sort((a, b) =>
    a.urgent !== b.urgent ? (a.urgent ? -1 : 1) : a.lastActivity.localeCompare(b.lastActivity),
  );
}

export function queueCounts(
  items: readonly InboxItem[],
  myId: string | undefined,
): Record<Queue, number> {
  const counts = {} as Record<Queue, number>;
  for (const { value } of QUEUES) {
    counts[value] = items.filter((item) => inQueue(item, value, myId)).length;
  }
  return counts;
}

/** How many open conversations have something this person has not seen — the nav badge. */
export function unreadOpenCount(
  items: readonly InboxItem[],
  seen: Readonly<Record<string, string>>,
): number {
  return items.filter((item) => matchesFilter(item, "open") && isUnread(item, seen)).length;
}

/**
 * Unread = something happened after this person last had the thread open.
 * `seen` maps an item id to the ISO activity time they last looked at; an item
 * they have never opened is unread. SOS alerts that nobody has acknowledged are
 * always unread. Per-browser by design — see useSeenThreads.
 */
export function isUnread(item: InboxItem, seen: Readonly<Record<string, string>>): boolean {
  if (item.kind === "sos") return item.sos.stage === "open";
  const at = seen[item.id];
  return at === undefined || item.lastActivity > at;
}

/** Urgent first, then most recent activity first. Every SOS counts as urgent. */
export function compareInbox(a: InboxItem, b: InboxItem): number {
  if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
  return b.lastActivity.localeCompare(a.lastActivity);
}

export function toInboxItems(
  sos: readonly SosAlertView[],
  tickets: readonly SupportTicketRow[],
  bookingRefById: ReadonlyMap<string, string>,
  lastMessageByThread: ReadonlyMap<string, LastMessage> = new Map(),
): InboxItem[] {
  const sosItems: InboxItem[] = sos.map((alert) => ({
    kind: "sos",
    id: alert.id,
    ref: alert.ref,
    category: "sos",
    urgent: true,
    urgentLabel: alert.dangerLabel ? `SOS · ${alert.dangerLabel}` : "SOS",
    who: alert.therapistName,
    bookingRefs: alert.bookingRefs,
    statusLabel:
      alert.stage === "open" ? "Nobody has it yet" : `Being handled by ${alert.handlerName}`,
    lastActivity: alert.acknowledgedAt ?? alert.raisedAt,
    lastMessage: null,
    hubId: alert.hubId,
    assignedStaffId: null,
    sos: alert,
  }));

  const ticketItems: InboxItem[] = tickets.map((ticket) => {
    const bookingRef = ticket.booking_id ? bookingRefById.get(ticket.booking_id) : undefined;
    const lastMessage = lastMessageByThread.get(ticket.id) ?? null;
    return {
      kind: "ticket",
      id: ticket.id,
      ref: ticket.ref,
      category: ticket.category,
      urgent: ticket.urgent,
      urgentLabel: ticket.urgent ? urgentReasonLabel(ticket.urgent_reason) : null,
      // First name only — the view carries nothing else (data wall). A
      // staff-authored ticket names the staff member instead (0320).
      who:
        ticket.author === "staff"
          ? `${ticket.staff_first_name?.trim() || "Staff"} · ${ticket.staff_role ?? "staff"}`
          : ticket.client_first_name?.trim() || "A client",
      bookingRefs: bookingRef ? [bookingRef] : [],
      statusLabel: ticketStatusLabel(ticket),
      // A message does not touch the thread's own `updated_at`, so a busy
      // thread would sink; the newest of the two is what "recent" means.
      lastActivity:
        lastMessage && lastMessage.created_at > ticket.updated_at
          ? lastMessage.created_at
          : ticket.updated_at,
      lastMessage,
      hubId: ticket.hub_id,
      assignedStaffId: ticket.assigned_staff_id,
      ticket,
    };
  });

  return [...sosItems, ...ticketItems].sort(compareInbox);
}
